/**
 * Accounts Hook
 *
 * Custom hook for fetching Plaid accounts for the selected tenant.
 *
 * @fileoverview React hook for account list operations
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useUser } from '../Context/userContext';
import { apiJson } from '../lib/api';
import { toUserMessage } from '../lib/apiError';

/**
 * Account summary from backend
 */
export interface AccountSummary {
  id: string;
  accountId: string;
  name: string;
  officialName: string | null;
  type: string;
  subtype: string | null;
  mask: string | null;
  currentBalance: number | null;
  availableBalance: number | null;
  limitBalance: number | null;
  isoCurrencyCode: string | null;
  transactionCount?: number;
  /** Institution name from the connected bank */
  institutionName: string | null;
  /** Institution ID from Plaid */
  institutionId: string | null;
  /** PlaidToken ID for syncing specific bank */
  plaidTokenId: string | null;
}

/**
 * Options for useAccounts hook
 */
interface UseAccountsOptions {
  /** Whether to auto-fetch on mount (default: true) */
  autoFetch?: boolean;
}

/**
 * Return type for useAccounts hook
 */
interface UseAccountsReturn {
  accounts: AccountSummary[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Hook for fetching accounts for the selected tenant
 *
 * @param options - Optional configuration
 * @param options.autoFetch - Whether to auto-fetch on mount (default: true)
 */
export function useAccounts(options: UseAccountsOptions = {}): UseAccountsReturn {
  const { autoFetch = true } = options;
  const { session } = useAuthContext();
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; });
  const hasSession = !!session?.access_token;
  const { selectedTenant, plaidTokens = [] } = useUser();
  const plaidTokenCount = plaidTokens.length;
  const prevTokenCountRef = useRef(plaidTokenCount);

  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAccounts = useCallback(async (): Promise<void> => {
    if (!sessionRef.current?.access_token || !selectedTenant) {
      setAccounts([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await apiJson<AccountSummary[]>(
        '/api/plaid-transactions/accounts',
        {
          headers: {
            'x-tenant-id': selectedTenant.id,
          },
        },
        sessionRef.current!.access_token
      );

      setAccounts(data);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load accounts. Please try again."));
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedTenant?.id]);

  const fetchRef = useRef(fetchAccounts);
  fetchRef.current = fetchAccounts;

  useEffect(() => {
    if (!autoFetch || !hasSession || !selectedTenant?.id) return;

    // Detect when a new bank was just connected (token count increased)
    const justConnected = plaidTokenCount > prevTokenCountRef.current;
    prevTokenCountRef.current = plaidTokenCount;

    // Immediate fetch
    fetchRef.current();

    if (justConnected) {
      // Poll up to 90s to catch accounts created by the async initial sync.
      // BullMQ initial sync has 5 retries with exponential backoff (~60s worst case).
      // Effect cleanup cancels pending timers if component unmounts.
      const timers = [
        setTimeout(() => fetchRef.current(), 2000),
        setTimeout(() => fetchRef.current(), 5000),
        setTimeout(() => fetchRef.current(), 10000),
        setTimeout(() => fetchRef.current(), 20000),
        setTimeout(() => fetchRef.current(), 35000),
        setTimeout(() => fetchRef.current(), 60000),
        setTimeout(() => fetchRef.current(), 90000),
      ];
      return () => timers.forEach(clearTimeout);
    }
  }, [autoFetch, hasSession, selectedTenant?.id, plaidTokenCount]);

  return {
    accounts,
    isLoading,
    error,
    refresh: fetchAccounts,
  };
}

export default useAccounts;
